package com.imobly.imobly.ui.screens.show.showpayments

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.compose.rememberNavController
import com.imobly.imobly.domain.MonthlyInstallment
import com.imobly.imobly.domain.Payment
import com.imobly.imobly.domain.enums.PaymentStatusEnum
import com.imobly.imobly.ui.components.button.ButtonComp
import com.imobly.imobly.ui.components.title.TitleComp
import com.imobly.imobly.ui.components.topbar.TopBarComp
import com.imobly.imobly.ui.theme.colors.BackGroundColor
import com.imobly.imobly.ui.theme.colors.CancelColor
import com.imobly.imobly.ui.theme.colors.ConfirmColor
import com.imobly.imobly.ui.theme.colors.PrimaryColor
import com.imobly.imobly.ui.theme.colors.TitleColor
import com.imobly.imobly.ui.theme.fonts.montserratFont
import com.imobly.imobly.viewmodel.PaymentViewModel
import com.imobly.imobly.viewmodel.SharedRepository
import org.jetbrains.compose.ui.tooling.preview.Preview

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ShowPaymentsScreen(paymentViewModel: PaymentViewModel) {

    LaunchedEffect(Unit) {
        if (SharedRepository.payment != null) {
            paymentViewModel.payment.value = SharedRepository.payment!!
            if (paymentViewModel.payment.value.lease.id != null) {
                paymentViewModel.currentLeaseId = paymentViewModel.payment.value.lease.id
            }
        }
    }
    val searchText by paymentViewModel.searchText

    val selectedStatus by paymentViewModel.selectedStatus
    var targetStatus by remember { mutableStateOf(PaymentStatusEnum.PAID) }
    var expandedDropdown by remember { mutableStateOf(false) }

    val filteredList = paymentViewModel.payment.value.installments.filter { installment ->
        searchText.isBlank() ||
                installment.status.label.contains(searchText, ignoreCase = true) ||
                installment.dueDate.contains(searchText, ignoreCase = true) ||
                installment.monthlyRent.contains(searchText)
    }

    Scaffold(
        topBar = { TopBarComp() },
        snackbarHost = { SnackbarHost(paymentViewModel.snackMessage.value) },
        contentWindowInsets = WindowInsets.systemBars
    ) { paddingValues ->

        Column(
            modifier = Modifier
                .background(BackGroundColor)
                .padding(paddingValues)
                .fillMaxSize()
        ) {

            TitleComp("Pagamentos", { paymentViewModel.goToShowLeases() }, true)

            Spacer(modifier = Modifier.height(10.dp))

            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center,
            ) {
                FilterChipComp(
                    selectedStatus = selectedStatus,
                    onStatusSelected = { paymentViewModel.setStatusFilter(it) }
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Batch action panel
            if (paymentViewModel.selectedInstallments.isNotEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center,
                ) {
                    Card(
                        modifier = Modifier
                            .padding(horizontal = 20.dp, vertical = 8.dp)
                            .widthIn(max = 600.dp)
                            .fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        shape = RoundedCornerShape(12.dp),
                        elevation = CardDefaults.cardElevation(2.dp)
                    ) {
                        Column(
                            modifier = Modifier.padding(16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = "${paymentViewModel.selectedInstallments.size} parcela(s) selecionada(s)",
                                fontFamily = montserratFont(),
                                fontWeight = FontWeight.Bold,
                                fontSize = 14.sp,
                                color = TitleColor
                            )
                            Spacer(modifier = Modifier.height(10.dp))
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.CenterHorizontally),
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                ExposedDropdownMenuBox(
                                    expanded = expandedDropdown,
                                    onExpandedChange = { expandedDropdown = !expandedDropdown },
                                    modifier = Modifier.weight(1f)
                                ) {
                                    OutlinedTextField(
                                        value = targetStatus.label,
                                        onValueChange = {},
                                        readOnly = true,
                                        label = { Text("Novo Status") },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedDropdown) },
                                        modifier = Modifier.menuAnchor().fillMaxWidth()
                                    )
                                    ExposedDropdownMenu(
                                        expanded = expandedDropdown,
                                        onDismissRequest = { expandedDropdown = false }
                                    ) {
                                        PaymentStatusEnum.entries.forEach { status ->
                                            DropdownMenuItem(
                                                text = { Text(status.label, fontFamily = montserratFont()) },
                                                onClick = {
                                                    targetStatus = status
                                                    expandedDropdown = false
                                                }
                                            )
                                        }
                                    }
                                }

                                Button(
                                    onClick = {
                                        paymentViewModel.updateSelectedStatus(targetStatus)
                                    },
                                    colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                                    shape = RoundedCornerShape(10.dp),
                                    modifier = Modifier.height(56.dp)
                                ) {
                                    Text("Atualizar", fontFamily = montserratFont(), fontWeight = FontWeight.Bold)
                                }
                            }
                        }
                    }
                }
            }

            if (paymentViewModel.messageError.value.isNotEmpty()) {
                Text(
                    text = paymentViewModel.messageError.value,
                    color = CancelColor,
                    fontFamily = montserratFont(),
                    fontSize = 14.sp,
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 4.dp)
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            LazyColumn(
                modifier = Modifier.fillMaxWidth().weight(1f),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {

                items(filteredList) { installment ->
                    val isSelected = paymentViewModel.selectedInstallments.contains(installment)
                    PaymentCard(
                        installment = installment,
                        isSelected = isSelected,
                        onCheckedChange = { paymentViewModel.toggleSelection(installment) }
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            val totalPages = paymentViewModel.payment.value.totalPages
            if (totalPages > 1) {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                    contentAlignment = Alignment.Center
                ) {
                    LazyRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        items(totalPages) { pageIndex ->
                            val isSelected = paymentViewModel.currentPage.value == pageIndex
                            Surface(
                                onClick = { paymentViewModel.loadPage(pageIndex) },
                                shape = RoundedCornerShape(50),
                                color = if (isSelected) PrimaryColor else Color.White,
                                shadowElevation = 2.dp,
                                modifier = Modifier.size(40.dp),
                                border = if (!isSelected) BorderStroke(1.dp, Color(0xFFCCCCCC)) else null
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    Text(
                                        text = "${pageIndex + 1}",
                                        color = if (isSelected) Color.White else TitleColor,
                                        fontFamily = montserratFont(),
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 14.sp
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun PaymentCard(installment: MonthlyInstallment, isSelected: Boolean, onCheckedChange: () -> Unit) {

    Card(
        onClick = onCheckedChange,
        modifier = Modifier
            .padding(horizontal = 20.dp, vertical = 4.dp)
            .widthIn(max = 700.dp)
            .fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = if (isSelected) Color(0xFFE3F2FD) else Color.White
        ),
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(4.dp)
    ) {

        Row(
            modifier = Modifier.padding(20.dp).fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.weight(1f)) {

                Text(
                    text = "Valor: R$ ${installment.monthlyRent}",
                    fontWeight = FontWeight.Bold,
                    fontFamily = montserratFont(),
                    fontSize = 20.sp,
                    color = Color(0xFF2D5B7A)
                )

                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "Status: ${installment.status}",
                        fontSize = 14.sp,
                        color = when (installment.status) {
                            PaymentStatusEnum.PAID -> ConfirmColor
                            PaymentStatusEnum.PENDING -> Color(0xFFF2603F)
                            else -> CancelColor
                        },
                        fontWeight = FontWeight.Bold,
                        fontFamily = montserratFont()
                    )

                    Text(
                        text = "Vencimento: ${installment.dueDate}",
                        fontSize = 14.sp,
                        color = Color(0xFF444444),
                        fontFamily = montserratFont()
                    )
                }

                Spacer(modifier = Modifier.height(6.dp))

                Text(
                    text = "Mês: ${installment.month.label}",
                    fontSize = 14.sp,
                    color = Color(0xFF777777),
                    fontFamily = montserratFont()
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            Checkbox(
                checked = isSelected,
                onCheckedChange = { onCheckedChange() },
                colors = CheckboxDefaults.colors(checkedColor = PrimaryColor)
            )
        }
    }
}


@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FilterChipComp(selectedStatus: PaymentStatusEnum?, onStatusSelected: (PaymentStatusEnum?) -> Unit) {

    val filters = PaymentStatusEnum.entries

    Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 10.dp).fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {

        Text(
            text = "Filtrar por status:",
            fontSize = 16.sp,
            fontFamily = montserratFont(),
            color = Color(0xFF333333),
            modifier = Modifier.padding(bottom = 12.dp)
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.CenterHorizontally),
            modifier = Modifier.fillMaxWidth(),
        ) {
            filters.forEach { filter ->

                val isSelected = selectedStatus == filter

                FilterChip(
                    selected = isSelected,
                    onClick = {
                        onStatusSelected(filter)
                    },
                    label = {
                        Text(
                            text = filter.label,
                            fontFamily = montserratFont(),
                            fontWeight = FontWeight.SemiBold
                        )
                    },
                    leadingIcon = {
                        if (isSelected)
                            Icon(
                                Icons.Default.Check,
                                contentDescription = null,
                                tint = Color.White
                            )
                    },
                    modifier = Modifier.height(40.dp),
                    shape = RoundedCornerShape(14.dp),
                    colors = FilterChipDefaults.filterChipColors(
                        selectedContainerColor = PrimaryColor,
                        selectedLabelColor = Color.White,
                        containerColor = BackGroundColor,
                        labelColor = TitleColor
                    ),
                    border = if (isSelected) null else
                        FilterChipDefaults.filterChipBorder(
                            borderColor = Color(0xFFBBBBBB),
                            enabled = true,
                            selected = isSelected
                        )
                )
            }
        }
    }
}

@Preview
@Composable
fun ShowPaymentsPreview() {
    val navFake = rememberNavController()
    ShowPaymentsScreen(PaymentViewModel(navFake))
}
